import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Heart, ShoppingCart, Trash2 } from 'lucide-react'
import TopHeader from '../components/layout/TopHeader.jsx'
import { ProductImage, getCategoryEmoji } from '../components/ui/index.jsx'
import { wishlistService } from '../services/api.js'
import { useCartStore, useWishlistStore, useAuthStore } from '../store/index.js'
import toast from 'react-hot-toast'

function WishlistItem({ item, onRemove, onAddToCart }) {
  const [qty, setQty] = useState(1)

  return (
    <div className="bg-surface-card rounded-2xl border border-surface-border overflow-hidden">
      <div className="flex gap-3 p-3">
        {/* Image */}
        <div
          className="w-20 h-20 bg-surface-elevated rounded-xl flex-shrink-0 overflow-hidden cursor-pointer"
          onClick={() => onAddToCart(item, 0)} // tap image → go to product
        >
          <ProductImage product={item} className="w-full h-full object-contain p-1" />
        </div>

        {/* Details */}
        <div className="flex-1 min-w-0">
          <p className="text-[10px] text-gray-500 font-display">
            {getCategoryEmoji(item)} {item.category_name || item.category}
          </p>
          <p className="text-sm text-white font-display font-medium leading-tight line-clamp-2 mt-0.5">
            {item.name}
          </p>
          <p className="text-gold font-display font-semibold text-sm mt-1">
            R{(item.price || 0).toFixed(2)}
          </p>
        </div>

        {/* Remove */}
        <button
          onClick={() => onRemove(item)}
          className="w-8 h-8 rounded-xl bg-red-900/20 flex items-center justify-center flex-shrink-0 self-start"
        >
          <Trash2 size={14} className="text-red-400" />
        </button>
      </div>

      {/* Qty + Add to cart */}
      <div className="flex items-center gap-2 px-3 pb-3">
        <div className="flex items-center bg-surface-elevated rounded-lg border border-surface-border overflow-hidden flex-shrink-0">
          <button
            onClick={() => setQty(q => Math.max(1, q - 1))}
            className="w-8 h-8 flex items-center justify-center text-gray-300 active:bg-surface-border text-lg leading-none"
          >−</button>
          <span className="w-8 text-center text-sm font-display font-semibold text-white">{qty}</span>
          <button
            onClick={() => setQty(q => q + 1)}
            className="w-8 h-8 flex items-center justify-center text-gray-300 active:bg-surface-border text-lg leading-none"
          >+</button>
        </div>
        <button
          onClick={() => onAddToCart(item, qty)}
          className="flex-1 h-8 bg-primary rounded-lg flex items-center justify-center gap-1.5 text-white text-xs font-display font-semibold active:bg-primary-dark transition-colors"
        >
          <ShoppingCart size={13} /> Add to Cart
        </button>
      </div>
    </div>
  )
}

export default function Wishlist() {
  const navigate    = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const addItem     = useCartStore(s => s.addItem)
  const localStore  = useWishlistStore()

  // Start with local store items so there's no empty flash on load
  const [items, setItems]     = useState(() => localStore.items || [])
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    if (!isAuthenticated) { setLoading(false); return }
    try {
      const data = await wishlistService.get()
      const serverItems = Array.isArray(data) ? data : (data?.items || [])

      // Backend returns product fields FLAT on each item (not nested under .product)
      // Fields: id (wishlist_item_id), product_id, description, stock_code,
      //         primary_image, inclprice2..5, brand, active
      const shaped = serverItems.map(i => ({
        id:            i.product_id || i.product?.id,
        wishItemId:    i.id,
        name:          i.product?.description || i.description || i.product?.stock_code || i.stock_code || 'Product',
        price:         i.product?.inclprice2 ?? i.inclprice2 ?? i.product?.inclprice ?? 0,
        primary_image: i.product?.primary_image || i.primary_image || null,
        image:         i.product?.image         || i.image         || null,
        stock_code:    i.product?.stock_code    || i.stock_code    || '',
        category_name: i.product?.category_name || i.category_name || '',
        brand:         i.product?.brand         || i.brand         || null,
        inStock:       true,
      }))

      setItems(shaped)
      // Sync local Zustand store directly to server state (no API calls fired)
      localStore.syncFromServer(shaped)
    } catch {
      // Keep showing local store items on error
      setItems(localStore.items || [])
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated])

  useEffect(() => { load() }, [load])

  const handleRemove = async (item) => {
    // Optimistic
    setItems(prev => prev.filter(i => i.id !== item.id))
    localStore.toggle(item)   // remove from local store too
    try {
      if (item.wishItemId) {
        await wishlistService.remove(item.wishItemId)
      } else {
        await wishlistService.removeByProduct(item.id)
      }
      toast.success('Removed from wishlist')
    } catch {
      // Revert on failure
      setItems(prev => [...prev, item])
      localStore.toggle(item)
      toast.error('Failed to remove')
    }
  }

  const handleAddToCart = (item, qty) => {
    if (qty === 0) {
      // Navigate to product detail
      navigate(`/product/${item.id}`)
      return
    }
    addItem(item, qty)
    toast.success(`${qty}× added to cart`, { duration: 1500 })
  }

  return (
    <div className="page-container bg-surface-dark">
      <TopHeader title="Wishlist" showBack backTo="/account" />

      {loading ? (
        <div className="flex justify-center pt-16">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center px-8">
          <Heart size={48} className="text-gray-600 mb-4" />
          <p className="font-heading text-white text-xl mb-1">Your wishlist is empty</p>
          <p className="font-display text-gray-400 text-sm mb-6">
            Save products you love to buy them later
          </p>
          <button onClick={() => navigate('/shop')} className="btn-primary py-3 px-6">
            Browse Products
          </button>
        </div>
      ) : (
        <div className="px-4 pt-2 pb-8 space-y-3">
          <p className="text-gray-500 text-xs font-display">{items.length} saved item{items.length !== 1 ? 's' : ''}</p>
          {items.map(item => (
            <WishlistItem
              key={item.id}
              item={item}
              onRemove={handleRemove}
              onAddToCart={handleAddToCart}
            />
          ))}
        </div>
      )}
    </div>
  )
}
